import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Local components
import MenuItem from '../MenuItem';

// Styled
import * as S from '../MenuItem/styled';

const MenuGroup = ({ customerId, title, icon, items }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const pathSegments = pathname.split('/').filter(Boolean);

  // Bepaal het meest specifieke matchende item
  const matchedItem = items
    .filter((item) => {
      const itemSegments = item.to.split('/').filter(Boolean);
      return (
        pathSegments.length >= itemSegments.length &&
        itemSegments.every((seg, i) => pathSegments[i] === seg)
      );
    })
    .sort((a, b) => b.to.length - a.to.length)[0];

  const active = matchedItem !== undefined;

  const [open, setOpen] = useState(active);
  const groupRef = useRef(null);
  const openedByUser = useRef(false);
  const onClick = useCallback(() => {
    openedByUser.current = !open;
    setOpen(!open);
  }, [open]);

  useEffect(() => {
    if (active) {
      setOpen(true);
    }
  }, [active]);

  useEffect(() => {
    if (!open || !openedByUser.current || !groupRef.current) return undefined;

    const timer = setTimeout(() => {
      groupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 420);

    return () => clearTimeout(timer);
  }, [open]);

  return (
    <S.MenuGroup ref={groupRef}>
      <S.Toggle onClick={onClick} open={open} active={active}>
        <>
          {icon && (
            <S.IconCircle active={active}>
              <FontAwesomeIcon icon={icon} />
            </S.IconCircle>
          )}
          <span>{t(`core:menu.items.${title}`, title)}</span>
          <FontAwesomeIcon icon={byPrefixAndName.fal['chevron-right']} size="xs" />
        </>
      </S.Toggle>
      <S.SubMenu visible={open}>
        <ul>
          {items.map((item) => (
            <MenuItem
              key={item.to}
              customerId={customerId}
              title={item.title}
              to={item.to}
              isActive={matchedItem?.to === item.to}
              nested
            />
          ))}
        </ul>
      </S.SubMenu>
    </S.MenuGroup>
  );
};

MenuGroup.propTypes = {
  customerId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
};

export default MenuGroup;
